import z from 'zod';
import { LandlordAddressType } from '../types/landlord';
import { zPhone } from './contacts';
import { ContactNumberType } from '../types/tenancies';

// istanbul ignore next
function emptyToNull(arg: unknown) {
    if (typeof arg !== 'string') {
        return arg;
    }
    if (arg.trim() === '') {
        return null;
    }
    return arg;
}

const zLandlordAustralianAddress = z.object({
    type: z.literal(LandlordAddressType.Australian),
    addressId: z.coerce.number().int().positive().gt(0)
        .finite()
        .optional()
        .nullable()
        .default(null),
    line1: z.string().min(1, 'Address line 1 is required'),
    line2: z.string().optional().nullable().default(null),
    line3: z.string().optional().nullable().default(null),
    suburbId: z.coerce.number({
        invalid_type_error: 'Suburb is required',
    }).int().positive().gt(0)
        .finite(),
    suburb: z.string(),
});

const zLandlordInternationalAddress = z.object({
    type: z.literal(LandlordAddressType.International),
    internationalAddress: z.string(),
});

const zLandlordNoAddress = z.object({
    type: z.literal(LandlordAddressType.None),
});

const zLandlordAddress = z.discriminatedUnion('type', [
    zLandlordAustralianAddress,
    zLandlordInternationalAddress,
    zLandlordNoAddress,
]);

const zLandlord = z.object({
    name: z.string()
        .min(1, 'Name is required')
        .max(75, 'Name must not be longer than 75 characters'),
    email: z.preprocess(emptyToNull, z.string()
        .email()
        .max(75, 'Email must not be longer than 75 characters')
        .optional()
        .nullable()
        .default(null)),
    salutation: z.string().optional().nullable().default(null),
    contactNumbers: z.array(
        z.object({
            contactNumberId: z.number()
                .int()
                .positive()
                .optional()
                .nullable()
                .default(null),
            number: zPhone.pipe(z.string().min(1, 'Contact number is required')),
            type: z.nativeEnum(ContactNumberType),
        })
    ),
    address: zLandlordAddress,
});

export { zLandlordAddress, zLandlord };
